import React, { useRef, useState } from "react";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { KeyOutlined } from "@ant-design/icons";
import { fetchPasskeyRegistrationOptions, verifyPasskeyRegistration } from "../../../api/auth/mfaApi";
import { authFailure, authFailureMessage, fieldError, hasFieldError, isStepUpDeclined } from "../../../lib/http/authErrors";
import { createPasskeyCredential, PasskeyCeremonyError, passkeyFailureMessage, suggestedPasskeyName } from "../../../lib/webauthn/webauthn";
import type { AttestationCredentialJSON } from "../../../types/auth/mfa";
import StepUpForm from "../../../components/auth/mfa/StepUpForm";
import { normaliseOtp } from "../../../components/auth/mfa/codes";
import EnrolmentEmailCode from "./EnrolmentEmailCode";
import { needsFinishingStepUp } from "./securityModel";

const { Paragraph } = Typography;

interface PasskeySetupProps {
	requireEmailCode: boolean;
	onDone: () => void;
	onCancel?: () => void;
}

interface PendingCeremony {
	flowId: string;
	credential: AttestationCredentialJSON;
}

// options → navigator.credentials.create → verify (contract §6.8); registration does not upgrade the session, so a second ceremony may follow.
const PasskeySetup: React.FC<PasskeySetupProps> = ({ requireEmailCode, onDone, onCancel }) => {
	const [form] = Form.useForm<{ name: string; email_code?: string }>();
	const [emailRequired, setEmailRequired] = useState(requireEmailCode);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [finishing, setFinishing] = useState(false);
	// A wrong email code keeps identity's flow alive, so the same ceremony is resubmitted rather than leaving an orphan credential on the device.
	const pending = useRef<PendingCeremony | null>(null);

	const register = async (values: { name: string; email_code?: string }) => {
		setBusy(true);
		setError("");
		try {
			if (!pending.current) {
				const { publicKey, flow_id } = await fetchPasskeyRegistrationOptions();
				pending.current = { flowId: flow_id, credential: await createPasskeyCredential(publicKey) };
			}
			const emailCode = emailRequired ? normaliseOtp(values.email_code ?? "") : undefined;
			await verifyPasskeyRegistration(pending.current.flowId, values.name.trim(), pending.current.credential, emailCode);
			pending.current = null;
			if (needsFinishingStepUp()) setFinishing(true);
			else onDone();
		} catch (err) {
			const failure = authFailure(err);
			if (err instanceof PasskeyCeremonyError) {
				setError(passkeyFailureMessage(err.reason));
			} else if (failure.status === 422 && hasFieldError(failure, "email_code")) {
				form.setFields([{ name: "email_code", errors: ["The email code is incorrect or has expired."] }]);
			} else if (failure.code === "mfa_enrolment_required" && failure.details.reason === "email_code_required") {
				setEmailRequired(true);
				setError("Enter the code we email you to finish.");
			} else if (isStepUpDeclined(err)) {
				pending.current = null;
				setError("Confirm it's you to add a sign-in method.");
			} else {
				pending.current = null;
				const credentialError = failure.status === 422 ? fieldError(failure, "credential") : undefined;
				setError(
					credentialError ??
						(failure.code === "challenge_expired" ? "The passkey request expired. Try again." : authFailureMessage(err, "The passkey could not be registered. Try again."))
				);
			}
		} finally {
			setBusy(false);
		}
	};

	if (finishing) {
		return (
			<StepUpForm
				methods={["passkey"]}
				onSuccess={onDone}
				submitLabel="Finish setup"
				intro={<Paragraph data-testid="security-passkey-finish">Passkey added. Confirm with it once more to finish.</Paragraph>}
			/>
		);
	}

	return (
		<div data-testid="security-passkey-setup">
			<Paragraph>A passkey signs you in with this device's fingerprint, face or screen lock instead of a password.</Paragraph>
			{error && <Alert data-testid="security-passkey-error" type="error" showIcon message={error} style={{ marginBottom: "var(--mg-sp-6)" }} />}
			<Form form={form} name="passkey-setup" layout="vertical" onFinish={register} initialValues={{ name: suggestedPasskeyName() }} requiredMark={false}>
				<Form.Item name="name" label="Passkey name" rules={[{ required: true, whitespace: true, message: "Name this passkey" }, { max: 64, message: "Use 64 characters or fewer" }]}>
					<Input data-testid="security-passkey-name" maxLength={64} disabled={busy} />
				</Form.Item>
				{emailRequired && <EnrolmentEmailCode onNotRequired={() => setEmailRequired(false)} disabled={busy} />}
				<Space wrap>
					<Button data-testid="security-passkey-create" type="primary" htmlType="submit" icon={<KeyOutlined />} loading={busy}>
						Create passkey
					</Button>
					{onCancel && (
						<Button onClick={onCancel} disabled={busy}>
							Cancel
						</Button>
					)}
				</Space>
			</Form>
		</div>
	);
};

export default PasskeySetup;
